/**
 * SouvenirPage.js
 *
 * /fly/souvenir — one link for every guest after the party. Each guest signs in and sees only their own
 * boarding pass and honorary passport from Flight YJ030, then can download both (as a zip of PNGs or PDFs)
 * and add the pass to their Wallet. Airport signs at the end lead to the other after-party pages.
 *
 * Who the guest is comes only from their sign-in: the backend matches the verified Firebase token to the
 * guest list. Nothing in the link decides whose documents are shown (admins may preview one with ?pnr=).
 */

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, FileText, Gift } from "lucide-react";
import toast from "react-hot-toast";
import { useClientAuth } from "../../hooks/clientAuth";
import SignUpAndInModal from "../../components/SignUp/SignUpAndInModal";
import BoardingPass, { usePassLayout } from "./components/BoardingPass";
import Passport, { PassportDrawing, usePassportLayout } from "./components/Passport";
import { AIRLINE_BLUE } from "./components/AirportSigns";
import { AfterPartySigns, Banner, bigButton, StatusCard } from "./components/AfterParty";
import { GOOGLE_WALLET_READY, getSouvenir, getSouvenirApplePassUrl, getSouvenirGooglePass, getSouvenirPhoto } from "./helpers/flyApi";
import { useNoIndex } from "./helpers/flyPage";
import { saveZip, useSnapshot } from "./helpers/snapshot";
import appleWalletBadge from "./assets/add-to-apple-wallet.svg";
import googleWalletBadge from "./assets/add-to-google-wallet.svg";

const INK = "#1B2A41";
const MUTED = "#5B6577";
const PAPER = "#FBF7EF";
const BACKDROP = "#E9E4DA";
const PASSPORT_BACKDROP = "#E3DCCF";

// Full-size widths of the boarding pass drawings, for the downloads
const WIDE_PASS_WIDTH = 1040;
const MOBILE_PASS_WIDTH = 358;

const walletsForDevice = () => {
    const agent = navigator.userAgent || "";
    if (/iPhone|iPad|iPod/.test(agent) || (/Macintosh/.test(agent) && navigator.maxTouchPoints > 1)) return ["apple"];
    if (/Android/i.test(agent)) return ["google"];
    return ["apple", "google"];
};

const fileName = (fullName, what) => `YJ030-${what}-${fullName.trim().replace(/\s+/g, "-")}`;

/**
 * Add to Apple Wallet (iPhone) / Google Wallet (Android, once Google approves the issuer)
 */
const WalletButtons = ({ adminPnr }) => {
    const [wallets] = useState(walletsForDevice);
    const [busy, setBusy] = useState(false);
    const shown = wallets.filter((w) => w === "apple" || GOOGLE_WALLET_READY);
    if (!shown.length) return null;

    const open = async (wallet) => {
        setBusy(true);
        try {
            window.location.href = wallet === "apple" ? await getSouvenirApplePassUrl(adminPnr) : (await getSouvenirGooglePass(adminPnr)).saveUrl;
        } catch (err) {
            toast.error(err.message || "Couldn't make your Wallet pass.");
        } finally {
            setBusy(false);
        }
    };

    const badge = "inline-flex rounded-[14px] transition hover:opacity-85 disabled:opacity-60";
    return (
        <div className="flex flex-wrap items-center justify-center gap-3">
            {shown.includes("apple") && (
                <button type="button" onClick={() => open("apple")} disabled={busy} className={badge}>
                    <img src={appleWalletBadge} alt="Add to Apple Wallet" className="h-14 w-auto" draggable={false} />
                </button>
            )}
            {shown.includes("google") && (
                <button type="button" onClick={() => open("google")} disabled={busy} className={badge}>
                    <img src={googleWalletBadge} alt="Add to Google Wallet" className="h-14 w-auto" draggable={false} />
                </button>
            )}
        </div>
    );
};

const Label = ({ children }) => (
    <h2 className="text-center text-xs font-bold tracking-[0.24em]" style={{ color: MUTED }}>{children}</h2>
);

const SouvenirPage = () => {
    const { user, loading: authLoading, signOutUser } = useClientAuth();
    const [searchParams] = useSearchParams();
    // Admins only: preview someone else's (the backend ignores it for everyone else)
    const adminPnr = searchParams.get("pnr")?.trim().toUpperCase() || undefined;
    const [showSignIn, setShowSignIn] = useState(false);
    // { state: 'loading' | 'found' | 'none' | 'error', data?, photo?, error? }
    const [result, setResult] = useState({ state: "loading" });
    const [reloadKey, setReloadKey] = useState(0);
    // 'png' or 'pdf' while the zip is being made
    const [saving, setSaving] = useState(null);
    const passLayout = usePassLayout();
    const passportLayout = usePassportLayout();
    const snapshot = useSnapshot();

    useNoIndex("Souvenirs · Flight YJ030");
    const uid = user?.uid;

    useEffect(() => {
        if (authLoading || !uid) return;
        let ignore = false;
        // Never keep showing one account's documents after switching accounts
        setResult({ state: "loading" });
        const load = async () => {
            try {
                const data = await getSouvenir(adminPnr);
                const photo = data.passport.hasPhoto ? await getSouvenirPhoto(adminPnr).catch(() => null) : null;
                if (!ignore) setResult({ state: "found", data, photo });
            } catch (err) {
                if (ignore) return;
                if (err.status === 404) setResult({ state: "none" });
                else setResult({ state: "error", error: err.message || "Something went wrong" });
            }
        };
        load();
        return () => { ignore = true; };
    }, [uid, authLoading, adminPnr, reloadKey]);

    const handleSignOut = async () => {
        try {
            await signOutUser();
        } catch (err) {
            toast.error("Couldn't sign out.");
        }
    };

    const signedInLine = user && (
        <p className="text-center text-xs" style={{ color: MUTED }}>
            Signed in as {user.email || user.displayName || "you"} ·{" "}
            <button type="button" onClick={handleSignOut} className="underline underline-offset-2">Sign out</button>
        </p>
    );

    let body;
    if (authLoading || (user && result.state === "loading")) {
        body = (
            <>
                <Banner icon={Gift} label="SOUVENIRS" />
                <p className="pt-24 text-center text-sm font-bold tracking-[0.24em] animate-pulse" style={{ color: MUTED }}>FINDING YOUR SOUVENIRS…</p>
            </>
        );
    } else if (!user) {
        body = (
            <>
                <Banner icon={Gift} label="SOUVENIRS" />
                <div className="px-3 pt-8">
                    <StatusCard title="SOUVENIRS">
                        <Gift size={40} color="#C8442F" aria-hidden="true" />
                        <h1 className="text-xl font-bold tracking-[0.04em]">Sign in to collect your boarding pass and passport</h1>
                        <p className="text-sm" style={{ color: MUTED }}>Use the same account you used for your Flight YJ030 invite.</p>
                        <button type="button" onClick={() => setShowSignIn(true)} className={bigButton} style={{ background: INK, color: PAPER }}>SIGN IN</button>
                    </StatusCard>
                </div>
            </>
        );
    } else if (result.state === "none") {
        body = (
            <>
                <Banner icon={Gift} label="SOUVENIRS" />
                <div className="px-3 pt-8 flex flex-col gap-4">
                    <StatusCard title="SOUVENIRS">
                        <Gift size={40} color="#C8442F" aria-hidden="true" />
                        <h1 className="text-xl font-bold">We couldn't find your booking on Flight YJ030.</h1>
                        <p className="text-sm" style={{ color: MUTED }}>Flew with a different account? Sign out and try that one. Otherwise, message Yash and he'll sort it out.</p>
                    </StatusCard>
                    {signedInLine}
                </div>
            </>
        );
    } else if (result.state === "error") {
        body = (
            <>
                <Banner icon={Gift} label="SOUVENIRS" />
                <div className="pt-16 px-4 flex flex-col items-center gap-4 text-center">
                    <p className="font-bold">We couldn't load your souvenirs.</p>
                    <p className="text-sm" style={{ color: MUTED }}>{result.error}</p>
                    <button type="button" onClick={() => setReloadKey((k) => k + 1)} className="min-h-[44px] text-xs font-bold tracking-[0.18em] underline underline-offset-4">TRY AGAIN</button>
                    {signedInLine}
                </div>
            </>
        );
    } else {
        const { pass, passport, isAdmin } = result.data;
        const firstName = pass.fullName.trim().split(/\s+/)[0];
        const accent = pass.colour;
        const width = Math.max(Math.min(passLayout.passWidth, 560), 300);

        // Both documents in one zip: the boarding pass as it looks on this screen, and the passport as the open book
        const saveBoth = async (format) => {
            setSaving(format);
            try {
                const mobile = passLayout.mobile;
                const passNode = (
                    <div style={{ width: mobile ? MOBILE_PASS_WIDTH : WIDE_PASS_WIDTH }}>
                        <BoardingPass pass={pass} layout={{ mobile, scale: 1 }} />
                    </div>
                );
                const passportNode = <PassportDrawing pass={pass} passport={passport} photoUrl={result.photo} stacked={false} />;
                const passBlob = await snapshot.capture(passNode, format, BACKDROP);
                const passportBlob = await snapshot.capture(passportNode, format, PASSPORT_BACKDROP);
                await saveZip([
                    { name: `${fileName(pass.fullName, "boarding-pass")}.${format}`, blob: passBlob },
                    { name: `${fileName(pass.fullName, "passport")}.${format}`, blob: passportBlob },
                ], `${fileName(pass.fullName, `souvenirs-${format}`)}.zip`);
            } catch (err) {
                console.error("Couldn't make the souvenirs zip:", err);
                toast.error("Couldn't download your souvenirs. Please try again.");
            } finally {
                setSaving(null);
            }
        };

        body = (
            <>
                <Banner icon={Gift} label="SOUVENIRS">
                    <h1 className="text-3xl sm:text-4xl font-bold mt-1">Welcome back, {firstName}</h1>
                    <p className="text-sm" style={{ color: MUTED }}>Your boarding pass and passport from Flight YJ030.</p>
                    {isAdmin && adminPnr && <p className="text-xs font-bold tracking-[0.18em]" style={{ color: "#C8442F" }}>ADMIN PREVIEW · {pass.pnr}</p>}
                </Banner>

                <div className="flex flex-col gap-4 pt-5">
                    <Label>BOARDING PASS</Label>
                    <BoardingPass pass={pass} layout={passLayout} />
                    <Label>PASSPORT</Label>
                    <Passport pass={pass} passport={passport} photoUrl={result.photo} layout={passportLayout} />

                    <div className="mx-auto mt-4 flex flex-col gap-4 px-2" style={{ width }}>
                        <Label>DOWNLOAD BOTH</Label>
                        <div className="grid grid-cols-2 gap-3">
                            <button type="button" disabled={!!saving} onClick={() => saveBoth("png")} className={bigButton} style={{ background: accent, color: PAPER }}>
                                <Download size={18} aria-hidden="true" /> {saving === "png" ? "SAVING…" : "PNG"}
                            </button>
                            <button type="button" disabled={!!saving} onClick={() => saveBoth("pdf")} className={bigButton} style={{ border: `2px solid ${accent}`, color: accent }}>
                                <FileText size={18} aria-hidden="true" /> {saving === "pdf" ? "SAVING…" : "PDF"}
                            </button>
                        </div>
                        <p className="-mt-2 text-center text-xs" style={{ color: MUTED }}>A zip with your boarding pass and passport.</p>
                        <WalletButtons adminPnr={adminPnr} />
                        {signedInLine}
                    </div>
                </div>
            </>
        );
    }

    // Every state ends with the way out, in the guest's colour once we know it
    const colour = result.state === "found" ? result.data.pass.colour : AIRLINE_BLUE;
    const signs = (
        <div className="mx-auto mt-10 w-full max-w-xl px-3">
            <AfterPartySigns current="souvenirs" colour={colour} />
        </div>
    );

    return (
        <div className="min-h-screen w-full overflow-x-hidden pb-14" style={{ background: BACKDROP, color: INK, fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
            {body}
            {signs}
            {snapshot.drawing}
            <SignUpAndInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} />
        </div>
    );
};

export default SouvenirPage;
